import type { FeatureCode, ProductCode } from '../constants';

/**
 * Perfiles de uso ("verticales").
 *
 * Al crear un tenant (workspace) el formulario pregunta para qué se va a usar
 * la cuenta: abogado, ingeniero civil, arquitecto, contador, familia, fotografía…
 * El perfil elegido determina, sin escribir reglas en React:
 *
 *   - el árbol de carpetas que se crea automáticamente,
 *   - la terminología del interfaz,
 *   - los campos de metadatos sugeridos,
 *   - las funciones que se destacan en el dashboard,
 *   - los valores por defecto de compartición (caducidad, contraseña…).
 *
 * Este catálogo es la fuente de verdad y se siembra en `workspace_profiles`.
 */

export interface FolderTemplateNode {
  name: string;
  children?: FolderTemplateNode[];
}

export type MetadataFieldType = 'text' | 'number' | 'date' | 'select' | 'multiselect';

export interface VerticalMetadataField {
  key: string;
  label: string;
  type: MetadataFieldType;
  options?: string[];
  required?: boolean;
  appliesTo: 'FILE' | 'FOLDER' | 'BOTH';
}

/**
 * Una casilla de una lista de verificación.
 *
 * `key` es la identidad estable dentro de la lista: la etiqueta puede
 * reescribirse sin que nada se rompa, la clave no. Es lo que permitirá a la
 * fase 15 decir «la casilla del documento de identidad» sin depender de cómo
 * esté redactada hoy.
 */
export interface ChecklistItemTemplate {
  key: string;
  label: string;
  /**
   * Sin él, la lista no está completa.
   *
   * Por defecto **sí** lo es: una casilla opcional es la excepción, no la
   * norma —quien escribe una lista de documentos es porque los necesita—.
   */
  required?: boolean;
  /** Qué documento es exactamente, cuando el nombre no basta. */
  hint?: string;
}

/**
 * Lista de verificación sugerida por profesión.
 *
 * Responde a la pregunta que un profesional se hace la mayoría de las veces:
 * **qué documentos necesito para esto**. No es inteligencia artificial, es una
 * lista — y este catálogo ya sabe guardar listas por profesión.
 *
 * Es una **sugerencia**, no una regla: al usarla se copia al espacio y a partir
 * de ahí es del titular, que añade, quita y reordena. Cambiar esta plantilla no
 * toca ninguna copia ya hecha, y es a propósito: una lista que se reescribe
 * sola bajo los pies de quien la está usando no sirve para trabajar.
 */
export interface ChecklistTemplate {
  /** Único en todo el catálogo; queda guardado en la copia como procedencia. */
  code: string;
  /** Perfil al que pertenece. Debe existir en el catálogo de perfiles. */
  profileCode: string;
  name: string;
  description: string;
  items: ChecklistItemTemplate[];
}

export type OnboardingQuestionType = 'select' | 'multiselect' | 'boolean' | 'text' | 'number';

export interface OnboardingOption {
  value: string;
  label: string;
}

/**
 * Preguntas adicionales que el formulario de alta muestra cuando se elige
 * este perfil. Las respuestas se guardan en `workspaces.profile_config`.
 */
export interface OnboardingQuestion {
  key: string;
  label: string;
  type: OnboardingQuestionType;
  options?: OnboardingOption[];
  required?: boolean;
  helpText?: string;
}

export interface SharingDefaults {
  /** Sugerir contraseña obligatoria en los enlaces públicos. */
  requirePassword: boolean;
  /** Caducidad por defecto de un enlace nuevo (días). `null` = sin caducidad. */
  expiresInDays: number | null;
  allowDownload: boolean;
  allowPreview: boolean;
}

/**
 * Agrupación del selector de perfiles.
 *
 * Con un catálogo amplio, una rejilla plana obliga a leerlo entero; las
 * categorías permiten que el formulario muestre primero el grupo y después el
 * perfil concreto.
 */
export const PROFILE_CATEGORY = {
  PERSONAL: 'PERSONAL',
  ENGINEERING: 'ENGINEERING',
  PROFESSIONAL_SERVICES: 'PROFESSIONAL_SERVICES',
  HEALTH: 'HEALTH',
  BUSINESS: 'BUSINESS',
  CREATIVE: 'CREATIVE',
  EDUCATION: 'EDUCATION',
} as const;

export type ProfileCategory = (typeof PROFILE_CATEGORY)[keyof typeof PROFILE_CATEGORY];

export const PROFILE_CATEGORY_LABEL: Record<ProfileCategory, string> = {
  PERSONAL: 'Personal y familia',
  ENGINEERING: 'Ingeniería, tecnología y construcción',
  PROFESSIONAL_SERVICES: 'Servicios profesionales',
  HEALTH: 'Salud',
  BUSINESS: 'Empresa y operaciones',
  CREATIVE: 'Creatividad y medios',
  EDUCATION: 'Educación y organizaciones',
};

/** Orden en que se presentan las categorías en el formulario. */
export const PROFILE_CATEGORY_ORDER: readonly ProfileCategory[] = [
  PROFILE_CATEGORY.PERSONAL,
  PROFILE_CATEGORY.ENGINEERING,
  PROFILE_CATEGORY.PROFESSIONAL_SERVICES,
  PROFILE_CATEGORY.BUSINESS,
  PROFILE_CATEGORY.HEALTH,
  PROFILE_CATEGORY.CREATIVE,
  PROFILE_CATEGORY.EDUCATION,
];

export interface WorkspaceProfile {
  code: string;
  productCode: ProductCode;
  category: ProfileCategory;
  name: string;
  description: string;
  /** Emoji usado como icono en el formulario de alta. */
  icon: string;
  sortOrder: number;
  /** Etiquetas que sustituyen a las genéricas del interfaz. */
  terminology: {
    workspace: string;
    folder: string;
    folderPlural: string;
    file: string;
    filePlural: string;
  };
  folderTemplate: FolderTemplateNode[];
  /** Funciones que el dashboard destaca para este perfil. */
  recommendedFeatures: FeatureCode[];
  metadataFields: VerticalMetadataField[];
  onboardingQuestions: OnboardingQuestion[];
  sharingDefaults: SharingDefaults;
  /** Extensiones típicas del sector; se usan para sugerencias y vistas previas. */
  commonExtensions: string[];
  /** Aviso mostrado en el alta cuando el sector maneja datos sensibles. */
  complianceNote?: string;
}
